import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useFieldArray } from 'react-final-form-arrays';
import { useCameraViewContext } from '../context';

export const useImageListScroll = (
  ref: React.RefObject<HTMLDivElement>,
  itemRefs: React.RefObject<HTMLDivElement[]>
) => {
  const { level1SectionId, imageIndex } = useCameraViewContext();
  const { fields } = useFieldArray(`${level1SectionId}.images`);

  const hasInitializedRef = useRef(false);

  /**
   * INITIAL POSITIONING
   * Runs before paint → no visible jump
   */
  useLayoutEffect(() => {
    console.log('layout effect', hasInitializedRef.current);
    if (hasInitializedRef.current) return;

    const list = ref.current;
    const items = itemRefs.current;

    if (!list || !items) return;

    // Case: active index is "add new" (past last)
    if (imageIndex === fields.length) {
      list.scrollLeft = list.scrollWidth;
      hasInitializedRef.current = true;
      return;
    }

    const item = items[imageIndex];
    if (!item) return;

    const listWidth = list.clientWidth;
    const itemWidth = item.clientWidth;
    const itemLeft = item.offsetLeft;

    const targetScroll = itemLeft - (listWidth / 2 - itemWidth / 2);

    const maxScroll = list.scrollWidth - listWidth;
    const clamped = Math.max(0, Math.min(targetScroll, maxScroll));

    list.scrollLeft = clamped;

    hasInitializedRef.current = true;
  }, [imageIndex, fields.length]);

  /**
   * SUBSEQUENT UPDATES
   * Smooth scrolling only after init
   */
  useEffect(() => {
    if (!hasInitializedRef.current) return;

    const list = ref.current;
    const items = itemRefs.current;

    if (!list || !items) return;

    console.log('reg effect', imageIndex, fields?.length, hasInitializedRef.current);

    // Same special case
    if (imageIndex === fields.length) {
      setTimeout(() => {
        list.scrollTo({
          left: list.scrollWidth,
          behavior: 'smooth'
        });
      }, 200);

      return;
    }

    const item = items[imageIndex];
    if (!item) return;

    const listWidth = list.clientWidth;
    const itemWidth = item.clientWidth;
    const itemLeft = item.offsetLeft;

    const targetScroll = itemLeft - (listWidth / 2 - itemWidth / 2);

    const maxScroll = list.scrollWidth - listWidth;
    const clamped = Math.max(0, Math.min(targetScroll, maxScroll));

    list.scrollTo({
      left: clamped,
      behavior: 'smooth'
    });
  }, [imageIndex, fields.length]);

  return { ref, itemRefs };
};
